import {
  Avatar,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { useMemo } from "react";
import {
  Assignee,
  AssigneeWorkload,
  SeveritySummary,
  StatusSummary,
  TypeSummary,
} from "./interface";
import TimerIcon from "@mui/icons-material/Timer";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import {
  COLORS_CHART,
  secondsToHours,
  SEVERITY_COLORS,
  STATUS_COLORS,
} from "./util";
import { ApexOptions } from "apexcharts";
import { ChartCard } from "./ChartCard";
import { KPICard } from "./KPICard";
import AssignmentIcon from "@mui/icons-material/Assignment";
interface SprintDetailViewProps {
  sprint: any;
  parentData?: any;
}

export default function SprintDetailView({
  sprint,
  parentData,
}: SprintDetailViewProps): JSX.Element {
  const theme = useTheme();
  const summary = sprint.sprintWiseSummary.sprintSummary;
  const sprintData = sprint.sprintWiseSummary;

  // Process status data
  const statusChartData = useMemo(() => {
    if (!sprintData.byStatus || sprintData.byStatus.length === 0) return [];
    return sprintData.byStatus.map((item: StatusSummary) => item.total);
  }, [sprintData.byStatus]);

  const statusLabels = useMemo(() => {
    if (!sprintData.byStatus || sprintData.byStatus.length === 0) return [];
    return sprintData.byStatus.map((item: StatusSummary) => item.statusCode);
  }, [sprintData.byStatus]);

  const statusColors = useMemo(() => {
    if (!sprintData.byStatus || sprintData.byStatus.length === 0) return [];
    return sprintData.byStatus.map(
      (item: StatusSummary) => STATUS_COLORS[item.statusCode] || "#999"
    );
  }, [sprintData.byStatus]);

  // Process severity data
  const severityChartData = useMemo(() => {
    if (!sprintData.bySeverity || sprintData.bySeverity.length === 0) return [];
    return sprintData.bySeverity.map((item: SeveritySummary) => item.total);
  }, [sprintData.bySeverity]);

  const severityLabels = useMemo(() => {
    if (!sprintData.bySeverity || sprintData.bySeverity.length === 0) return [];
    return sprintData.bySeverity.map((item: SeveritySummary) => item.severity);
  }, [sprintData.bySeverity]);

  const severityColors = useMemo(() => {
    if (!sprintData.bySeverity || sprintData.bySeverity.length === 0) return [];
    return sprintData.bySeverity.map(
      (item: SeveritySummary) => SEVERITY_COLORS[item.severity] || "#999"
    );
  }, [sprintData.bySeverity]);

  // Process type data
  const typeChartData = useMemo(() => {
    if (!sprintData.byType || sprintData.byType.length === 0) return [];
    return sprintData.byType.map((item: TypeSummary) => item.total);
  }, [sprintData.byType]);

  const typeLabels = useMemo(() => {
    if (!sprintData.byType || sprintData.byType.length === 0) return [];
    return sprintData.byType.map((item: TypeSummary) => item.type);
  }, [sprintData.byType]);

  // Process skill data with proper labels
  const skillChartData = useMemo(() => {
    if (
      !sprintData.byRequiredSkillSet ||
      sprintData.byRequiredSkillSet.length === 0
    )
      return { values: [], labels: [] };

    const processedData = sprintData.byRequiredSkillSet
      .map((item: SkillSummary) => ({
        name: item.skillSet || "Unknown",
        value: item.total,
      }))
      .filter((item) => item.name !== "Unknown" && item.value > 0);

    return {
      values: processedData.map((item) => item.value),
      labels: processedData.map((item) => item.name),
    };
  }, [sprintData.byRequiredSkillSet]);

  // Process assignee workload
  const assigneeWorkloadData = useMemo(() => {
    if (!sprintData.assigneeVsWorkload)
      return { categories: [], timeSpent: [], estimated: [] };
    const data = Object.values(sprintData.assigneeVsWorkload)
      .map((item: any) => ({
        name: item.workedBy?.fullName || item.assignTo?.fullName || "Unknown",
        timeSpent: secondsToHours(item.totalTimeSpend),
        estimated: secondsToHours(item.timeEstimation || 0),
        id: item.workedBy?._id || item.assignTo?._id,
      }))
      .filter((item) => item.name !== "Unknown")
      .sort((a, b) => b.timeSpent - a.timeSpent)
      .slice(0, 10);

    return {
      categories: data.map((d) => d.name),
      timeSpent: data.map((d) => d.timeSpent),
      estimated: data.map((d) => d.estimated),
    };
  }, [sprintData.assigneeVsWorkload]);

  const skillWorkloadData = useMemo(() => {
    if (!sprintData.skillSetVsWorkload)
      return { categories: [], timeSpent: [], estimated: [] };

    const data = Object.values(sprintData.skillSetVsWorkload)
      .filter(
        (item: any) =>
          item.skillType !== "Not Defined" && item.totalTimeSpendSkillWise > 0
      )
      .map((item: any) => ({
        name: item.skillType,
        timeSpent: secondsToHours(item.totalTimeSpendSkillWise),
        estimated: secondsToHours(item.timeEstimationSkillWise || 0),
      }))
      .sort((a, b) => b.timeSpent - a.timeSpent);

    return {
      categories: data.map((d) => d.name),
      timeSpent: data.map((d) => d.timeSpent),
      estimated: data.map((d) => d.estimated),
    };
  }, [sprintData.skillSetVsWorkload]);

  // ApexChart Options
  const pieOptions: ApexOptions = {
    chart: {
      type: "donut",
      width: "100%",
      fontFamily: "inherit",
    },
    labels: statusLabels,
    colors: statusColors,
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "12px",
      fontWeight: 500,
      offsetY: 5,
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "13px",
        fontWeight: "600",
      },
      formatter: function (val: number, opts: any) {
        return opts.w.config.series[opts.seriesIndex];
      },
      dropShadow: {
        enabled: false,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total",
              fontSize: "14px",
              fontWeight: 600,
              formatter: function (w: any) {
                return w.globals.seriesTotals.reduce(
                  (a: number, b: number) => a + b,
                  0
                );
              },
            },
          },
        },
      },
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return val.toString();
        },
      },
    },
    stroke: {
      width: 2,
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  };

  const severityBarOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      width: "100%",
      fontFamily: "inherit",
    },
    plotOptions: {
      bar: {
        borderRadius: 10,
        distributed: true,
        columnWidth: "70%",
      },
    },
    colors: severityColors,
    xaxis: {
      categories: severityLabels,
      labels: {
        rotate: -45,
        style: {
          fontSize: "11px",
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: "11px",
        },
        formatter: function (val: number) {
          return Math.floor(val).toString();
        },
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "11px",
        fontWeight: "600",
      },
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return val.toString();
        },
        title: {
          formatter: function (seriesName: string) {
            return "";
          },
        },
      },
    },
    grid: {
      borderColor: "#f1f1f1",
      strokeDashArray: 4,
    },
  };

  const typeBarOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      width: "100%",
      fontFamily: "inherit",
    },
    plotOptions: {
      bar: {
        borderRadius: 10,
        distributed: true,
        columnWidth: "70%",
      },
    },
    colors: COLORS_CHART,
    xaxis: {
      categories: typeLabels,
      labels: {
        rotate: -45,
        style: {
          fontSize: "11px",
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: "11px",
        },
        formatter: function (val: number) {
          return Math.floor(val).toString();
        },
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "11px",
        fontWeight: "600",
      },
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return val.toString();
        },
        title: {
          formatter: function (seriesName: string) {
            return "";
          },
        },
      },
    },
    grid: {
      borderColor: "#f1f1f1",
      strokeDashArray: 4,
    },
  };

  const skillBarOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      width: "100%",
      fontFamily: "inherit",
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        horizontal: true,
        distributed: true,
        barHeight: "75%",
      },
    },
    colors: COLORS_CHART,
    xaxis: {
      title: {
        text: "Number of Issues",
        style: {
          fontSize: "12px",
          fontWeight: 600,
        },
      },
      labels: {
        style: {
          fontSize: "11px",
        },
        formatter: function (val: string) {
          return Math.floor(Number(val)).toString();
        },
      },
    },
    yaxis: {
      //@ts-ignore
      categories: skillChartData.labels,
      labels: {
        style: {
          fontSize: "11px",
          fontWeight: 500,
        },
      },
    },
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "11px",
      fontWeight: 500,
      offsetY: 5,
      markers: {
        width: 10,
        height: 10,
        radius: 2,
      },
      itemMargin: {
        horizontal: 8,
        vertical: 4,
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "11px",
        fontWeight: "600",
        colors: ["#fff"],
      },
      formatter: function (val: number) {
        return Math.floor(val).toString();
      },
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return Math.floor(val).toString() + " issues";
        },
        title: {
          formatter: function (seriesName: string) {
            return "";
          },
        },
      },
    },
    grid: {
      borderColor: "#f1f1f1",
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true,
        },
      },
    },
  };

  const assigneeWorkloadOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      stacked: false,
      width: "100%",
      fontFamily: "inherit",
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        horizontal: false,
        columnWidth: "75%",
      },
    },
    colors: ["#FF6B6B", "#4CAF50"],
    xaxis: {
      categories: assigneeWorkloadData.categories,
      labels: {
        rotate: -45,
        style: {
          fontSize: "11px",
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      title: {
        text: "Hours",
        style: {
          fontSize: "12px",
          fontWeight: 600,
        },
      },
      labels: {
        style: {
          fontSize: "11px",
        },
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "right",
      fontSize: "13px",
      fontWeight: 600,
      offsetY: 0,
      markers: {
        width: 14,
        height: 14,
        radius: 4,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 0,
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val.toFixed(1)} hrs`,
      },
    },
    grid: {
      borderColor: "#f1f1f1",
      strokeDashArray: 4,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
  };

  const skillWorkloadOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      stacked: false,
      width: "100%",
      fontFamily: "inherit",
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        horizontal: true,
        barHeight: "70%",
      },
    },
    colors: ["#FF6B6B", "#4CAF50"],
    xaxis: {
      title: {
        text: "Hours",
        style: {
          fontSize: "12px",
          fontWeight: 600,
        },
      },
      labels: {
        style: {
          fontSize: "11px",
        },
      },
    },
    yaxis: {
      //@ts-ignore
      categories: skillWorkloadData.categories,
      labels: {
        style: {
          fontSize: "11px",
          fontWeight: 500,
        },
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "right",
      fontSize: "13px",
      fontWeight: 600,
      offsetY: 0,
      markers: {
        width: 14,
        height: 14,
        radius: 4,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 0,
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val.toFixed(1)} hrs`,
      },
    },
    grid: {
      borderColor: "#f1f1f1",
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true,
        },
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
  };

  return (
    <Box sx={{ pt: 2 }}>
      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Total Tasks"
            value={summary.totalBugs}
            icon={<AssignmentIcon sx={{ fontSize: 22 }} />}
            color="#9C27B0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Time Spent"
            value={secondsToHours(summary.totalTimeSpendInSprint)}
            unit="hrs"
            icon={<AccessTimeIcon sx={{ fontSize: 22 }} />}
            color="#FF6B6B"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Estimated Time"
            value={secondsToHours(summary.totalTimeEstimation)}
            unit="hrs"
            icon={<TimerIcon sx={{ fontSize: 22 }} />}
            color="#2196F3"
          />
        </Grid>
      </Grid>

      {/* Sprint Charts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statusChartData.length > 0 && (
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <ChartCard title="Tasks by Status" height={280}>
              <Box sx={{ width: "100%", height: "100%" }}>
                {parentData?.chartWrapper ? (
                  <parentData.chartWrapper
                    options={pieOptions}
                    series={statusChartData}
                    type="donut"
                    height={250}
                    width="100%"
                  />
                ) : null}
              </Box>
            </ChartCard>
          </Grid>
        )}

        {severityChartData.length > 0 && (
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <ChartCard title="Tasks by Severity" height={280}>
              <Box sx={{ width: "100%", height: "100%" }}>
                {parentData?.chartWrapper ? (
                  <parentData.chartWrapper
                    options={severityBarOptions}
                    series={[{ name: "Count", data: severityChartData }]}
                    type="bar"
                    height={250}
                    width="100%"
                  />
                ) : null}
              </Box>
            </ChartCard>
          </Grid>
        )}

        {typeChartData.length > 0 && (
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <ChartCard title="Tasks by Type" height={280}>
              <Box sx={{ width: "100%", height: "100%" }}>
                {parentData?.chartWrapper ? (
                  <parentData.chartWrapper
                    options={typeBarOptions}
                    series={[{ name: "Count", data: typeChartData }]}
                    type="bar"
                    height={250}
                    width="100%"
                  />
                ) : null}
              </Box>
            </ChartCard>
          </Grid>
        )}

        {skillChartData.values.length > 0 && (
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <ChartCard title="Issues by Skill" height={280}>
              <Box sx={{ width: "100%", height: "100%" }}>
                {parentData?.chartWrapper ? (
                  <parentData.chartWrapper
                    options={skillBarOptions}
                    series={[{ name: "Issues", data: skillChartData.values }]}
                    type="bar"
                    height={250}
                    width="100%"
                  />
                ) : null}
              </Box>
            </ChartCard>
          </Grid>
        )}
      </Grid>

      {/* Workload Charts */}
      <Grid container spacing={2}>
        {assigneeWorkloadData.categories.length > 0 && (
          <Grid item xs={12} lg={6}>
            <ChartCard title="Workload by Assignee" height={320}>
              <Box sx={{ width: "100%", height: "100%" }}>
                {parentData?.chartWrapper ? (
                  <parentData.chartWrapper
                    options={assigneeWorkloadOptions}
                    series={[
                      {
                        name: "Time Spent",
                        data: assigneeWorkloadData.timeSpent,
                      },
                      {
                        name: "Estimated",
                        data: assigneeWorkloadData.estimated,
                      },
                    ]}
                    type="bar"
                    height={280}
                    width="100%"
                  />
                ) : null}
              </Box>
            </ChartCard>
          </Grid>
        )}

        {skillWorkloadData.categories.length > 0 && (
          <Grid item xs={12} lg={6}>
            <ChartCard title="Workload by Skill" height={320}>
              <Box sx={{ width: "100%", height: "100%" }}>
                {parentData?.chartWrapper ? (
                  <parentData.chartWrapper
                    options={skillWorkloadOptions}
                    series={[
                      { name: "Time Spent", data: skillWorkloadData.timeSpent },
                      { name: "Estimated", data: skillWorkloadData.estimated },
                    ]}
                    type="bar"
                    height={280}
                    width="100%"
                  />
                ) : null}
              </Box>
            </ChartCard>
          </Grid>
        )}
      </Grid>

      {/* Team Members */}
      {summary.uniqueAssignee && summary.uniqueAssignee.length > 0 && (
        <Box sx={{ mb: 2.5, mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            Team Members ({summary.totalAssignee})
          </Typography>
          <Grid container spacing={2}>
            {summary.uniqueAssignee.map((assignee: Assignee) => {
              const workload = Object.values(
                sprintData.assigneeVsWorkload || {}
              ).find(
                (w: any) =>
                  (w.workedBy?._id || w.assignTo?._id) === assignee._id
              ) as AssigneeWorkload | undefined;

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={assignee._id}>
                  <Card
                    elevation={0}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        borderColor: theme.palette.primary.main + "40",
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 1.5,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            fontSize: "1rem",
                            bgcolor: theme.palette.primary.main,
                            fontWeight: 600,
                          }}
                        >
                          {assignee.fullName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {assignee.fullName}
                        </Typography>
                      </Box>
                      {workload && (
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Time Spent
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: "#FF6B6B" }}
                            >
                              {secondsToHours(workload.totalTimeSpend)} hrs
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Estimated
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: "#4CAF50" }}
                            >
                              {secondsToHours(workload.timeEstimation || 0)} hrs
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Box>
  );
}
